/*
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc.
 * All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

import Framework, { BaseService } from '@greenville/framework';
import * as constants from '../../constants/ServiceConstants';
import utils from '../../../../common/utils';
import { RUN_EVAL_API_PATH } from '../../constants/StudioTextConstants';
import {
  getErrorMessage,
  handleGraphTesterEvalError,
  publishErrorEvent,
  resolveLangMessage,
  setNotification
} from './graphServiceUtils';

const SKIPPED_RESULT = { evalLensSkipped: true };

/** POST /evaluation — Graph Tester eval criteria fetch. */
export default class RunEvaluationCriteriaService extends BaseService {
  constructor(
    eventName = constants.POST_RUN_EVAL_LENS_EVALUATION,
    statusStoreName = 'postRunEvalLensEvaluationStatus'
  ) {
    super(eventName, statusStoreName);
  }

  notifyError(messageOrLangKey, { isLangKey = false } = {}) {
    if (isLangKey) {
      const message = resolveLangMessage(messageOrLangKey);
      setNotification({ type: 'error', langMessage: messageOrLangKey });
      Framework.getStoreRegistry().getStore('graphTesterModel')?.setEvaluationCriteriaError(message);
    } else {
      setNotification({ type: 'error', message: messageOrLangKey });
      Framework.getStoreRegistry().getStore('graphTesterModel')?.setEvaluationCriteriaError(messageOrLangKey);
    }
    publishErrorEvent(constants.POST_RUN_EVAL_LENS_EVALUATION_ERROR);
  }

  validatePayload(payload, headers) {
    if (headers == null || Object.keys(headers).length === 0) {
      this.notifyError('graphEvaluation.MISSING_TENANT', { isLangKey: true });
      return false;
    }

    const { rubricId, requestId } = payload || {};
    if (rubricId == null || rubricId === '') {
      this.notifyError('graphEvaluation.MISSING_RUBRIC', { isLangKey: true });
      return false;
    }

    if (requestId == null || requestId === '') {
      this.notifyError('graphEvaluation.MISSING_REQUEST_ID', { isLangKey: true });
      return false;
    }

    return true;
  }

  async handleEvent(eventName, event) {
    const { payload, headers } = utils.buildRequestPartsFromPayload(event);

    if (!this.validatePayload(payload, headers)) {
      return SKIPPED_RESULT;
    }

    const requestBody = { rubricId: payload.rubricId };
    if (payload.requestId != null && payload.requestId !== '') {
      requestBody.requestId = payload.requestId;
    }

    const { isStackB } = utils.getStackBParams();
    const useStackBClient = payload?.useStackB === true || isStackB;

    if (useStackBClient) {
      const enhancedHeaders = await utils.resolveStackBHeaders(headers);
      return this.getClient('aiadminstackb').post(RUN_EVAL_API_PATH, requestBody, {
        headers: enhancedHeaders
      });
    }
    return this.getClient('aiadmin').post(RUN_EVAL_API_PATH, requestBody, { headers });
  }

  handleResponse(response) {
    if (response?.evalLensSkipped) {
      return;
    }

    const { data } = response;

    if (data && typeof data === 'object' && data.success === false) {
      this.notifyError(getErrorMessage(data, 'graphEvaluation.RUN_FAILED'), { isLangKey: false });
      return;
    }

    if (data == null) {
      this.notifyError('graphEvaluation.INVALID_RESPONSE', { isLangKey: true });
      return;
    }

    Framework.getStoreRegistry().getStore('graphTesterModel')?.setEvaluationCriteria(data);
    Framework.getEventManager().publish(constants.POST_RUN_EVAL_LENS_EVALUATION_SUCCESS, { data });
  }

  handleError(error) {
    handleGraphTesterEvalError(error, {
      fallbackLangMessage: 'graphEvaluation.FETCH_CRITERIA_FAILED',
      errorEvent: constants.POST_RUN_EVAL_LENS_EVALUATION_ERROR
    });
  }
}
